import { useState, useEffect, useRef, memo } from "react";
import clsx from "clsx";
import ReactPlayer from "react-player";

function AlertPlayer({
  videoUrl,
  from,
  autoPlay = true,
}: {
  videoUrl: string;
  from?: string;
  autoPlay?: boolean;
}) {
  const [reloadAttempts, setReloadAttempts] = useState(0);
  const [playerKey, setPlayerKey] = useState(0);
  const [playing, setPlaying] = useState(autoPlay);
  const [seekTo, setSeekTo] = useState(null); // State to hold seek position
  const [showError, setShowError] = useState(false);
  const playerRef = useRef(null); // Ref to access player instance

  useEffect(() => {
    setShowError(false); // Hide error modal on new URL
    if (videoUrl) {
      setReloadAttempts(0); // Reset reload attempts on new URL
      setPlayerKey((prevKey) => prevKey + 1); // Force remount on URL change
    }
  }, [videoUrl]);

  const handleError = (err, data) => {
    if (reloadAttempts < 4) {
      // Limit the number of reload attempts
      setPlayerKey((prevKey) => prevKey + 1); // Force remount to retry
      if (playerRef.current) {
        const currentTime = playerRef.current.getCurrentTime(); // Get current time
        setSeekTo(currentTime + 1); // Seek 1 second ahead of the error point
      }
      setReloadAttempts(reloadAttempts + 1);
      setPlaying(true); // Resume playback
    } else {
      //Handle repeated failures after 3 attempts
      setShowError(true); // Show error modal
      console.error("Failed to load video after multiple attempts.");
    }
  };

  useEffect(() => {
    if (seekTo !== null && playerRef.current) {
      playerRef.current.seekTo(seekTo, "seconds"); // Seek to the new position
      setSeekTo(null); // Reset seekTo state
    }
  }, [seekTo]);

  return (
    <div className="w-full h-full relative max-w-full max-h-full overflow-hidden">
      {videoUrl ? (
        <div
          className={clsx(
            "max-h-full max-w-full aspect-video relative overflow-hidden group top-1/2 left-1/2 transform translate-x-[-50%] translate-y-[-50%]",
            from == "basic" ? "rounded-2xl " : "",
          )}
        >
          <ReactPlayer
            config={{
              file: {
                attributes: {
                  crossOrigin: "true",
                },
              },
            }}
            ref={playerRef} // Attach ref to the player
            key={playerKey} // Use key to force remount on error
            url={videoUrl}
            controls={true}
            width="100%"
            height="100%"
            className="max-h-full max-w-full overflow-hidden"
            muted={true}
            playing={playing}
            onPlay={() => setReloadAttempts(0)}
            onError={handleError} // Handle errors
          />
        </div>
      ) : (
        <div className="w-full absolute inset-0 flex flex-col items-center justify-center gap-3">
          <div className="spinner" role="status"></div>
          <p className="text-neutral-500">No Video Available Yet...</p>
        </div>
      )}

      {showError && (
        <div className="absolute inset-0 bg-black bg-opacity-75 flex items-center justify-center">
          <div className="bg-white p-5 rounded shadow-lg text-center">
            <p className="mb-4">
              Failed to load video after multiple attempts. Please download the
              video to view it by clicking lower right coner of video.
            </p>
            <a href={videoUrl} download className="text-blue-500 underline">
              Download Video
            </a>
            <button
              className="mt-4 bg-blue-500 text-white px-4 py-2 rounded"
              onClick={() => setShowError(false)}
            >
              Close
            </button>
          </div>
        </div>
      )}
    </div>
  );
}

export default memo(AlertPlayer);
